import * as THREE from 'three';
import { UnitConversions } from '@/utils/units';

/**
 * Impact model for the controller-swing ("virtual golf") mode: measured club
 * delivery in, an `OpenGolfSim.Shot` out, so the existing flight sim and HUD
 * are fed exactly what a launch monitor would have sent.
 *
 * Everything here is a heuristic fit to real launch-monitor behaviour, not a
 * rigid-body collision solve — every coefficient lives in VIRTUAL_CLUBS or the
 * constants below so it can be tuned in one place. What the model does get
 * right is the *structure* of the D-plane: curve comes from face-to-path
 * relative to spin loft (so an open face slices a driver but barely bends a
 * 9 iron), start line is mostly face, and spin scales with spin loft × speed.
 *
 * Sign conventions match BallPhysics._launchFull and every launch monitor FUSE
 * talks to: positive = right of target, for both handednesses.
 *  - horizontalLaunchAngle > 0  → ball starts right
 *  - spinAxis > 0               → ball curves right
 *  - attackAngle > 0            → hitting up on the ball
 *  - clubPath > 0               → in-to-out for a right-hander
 *  - faceToTarget > 0           → face open (pointing right of target)
 */

export type ClubHeadShape = 'wood' | 'iron' | 'wedge';

export type ClubSpec = {
  name: string;
  /** Static loft, degrees. */
  loft: number;
  /** Grip (controller) to head, metres — drives the overlay and the head sweep. */
  length: number;
  /**
   * Heel → face-centre lateral offset, metres. The shaft enters a real club at
   * the HEEL; the face centre (the impact point) sits this far toe-ward of the
   * shaft line. Drives both where the physics puts the impact point relative to
   * the calibrated shaft and where the drawn head hangs off the drawn shaft.
   */
  faceOffset: number;
  /** Centre-strike, ideal-spin-loft smash factor (ball speed / club speed). */
  smash: number;
  /** rpm per (degree of spin loft × mph of club speed). Fitted ~2.2–2.6. */
  spinK: number;
  /** Fraction of dynamic loft that becomes launch angle before attack angle. */
  launchRatio: number;
  /** Weight of face angle in the start line; the rest comes from path. */
  faceBias: number;
  /** Scales face-to-path into spin-axis tilt. 1 = textbook D-plane. */
  axisGain: number;
  /** Degrees of face-to-path added per cm of toe-side strike (gear effect). */
  gearGain: number;
  /**
   * Club-speed assist, applied BEFORE smash factor.
   *
   * An indoor swing is not a golf swing. The player is standing in a living
   * room with a controller on a stick, not unloading a driver on a tee box, and
   * a compact careful pass produces club speed that would embarrass a real
   * driver — so a physically faithful model hands back 180-yard drives and the
   * long clubs feel pointless.
   *
   * Golf+ answers this by helping most where the gap is widest, and so does
   * this: the assist is largest on the driver, tapers through the irons, and is
   * exactly 1.0 at the wedges, where a real indoor swing already produces a
   * realistic speed and any help would just make short shots unplayable.
   *
   * Before smash rather than after, so everything downstream — spin loft,
   * strike quality, gear effect — still reads a coherent club speed.
   */
  speedGain: number;
  head: ClubHeadShape;
};

/**
 * Playable set for the range. Ordered long → short so the wrist-menu arrows
 * read like a bag. Numbers are fitted to tour/amateur launch-monitor averages:
 * e.g. Driver at 100 mph with +2° attack and 13° dynamic loft lands on
 * ~145 mph ball / ~11° launch / ~2400 rpm.
 */
/**
 * Where the per-club speed assist starts and where it is fully in.
 *
 * Below the first number the player is deliberately hitting something soft and
 * gets no help whatsoever. The second is roughly a committed indoor full swing
 * — past it there is nothing more to give, so a genuinely fast swing is not
 * double-counted.
 */
const ASSIST_START_MPH = 35;
const ASSIST_FULL_MPH = 70;

export const VIRTUAL_CLUBS: ClubSpec[] = [
  { name: 'Driver', loft: 10.5, length: 1.15, faceOffset: 0.044, smash: 1.48, spinK: 2.20, launchRatio: 0.80, faceBias: 0.85, axisGain: 1.00, gearGain: 0.55, speedGain: 1.35, head: 'wood' },
  { name: '3 Wood', loft: 15.0, length: 1.09, faceOffset: 0.042, smash: 1.45, spinK: 2.30, launchRatio: 0.80, faceBias: 0.82, axisGain: 0.95, gearGain: 0.40, speedGain: 1.30, head: 'wood' },
  { name: '5 Iron', loft: 27.0, length: 0.97, faceOffset: 0.038, smash: 1.38, spinK: 2.40, launchRatio: 0.78, faceBias: 0.78, axisGain: 0.90, gearGain: 0.15, speedGain: 1.15, head: 'iron' },
  { name: '7 Iron', loft: 34.0, length: 0.94, faceOffset: 0.038, smash: 1.33, spinK: 2.42, launchRatio: 0.76, faceBias: 0.76, axisGain: 0.90, gearGain: 0.12, speedGain: 1.10, head: 'iron' },
  { name: '9 Iron', loft: 41.0, length: 0.91, faceOffset: 0.038, smash: 1.28, spinK: 2.45, launchRatio: 0.74, faceBias: 0.75, axisGain: 0.88, gearGain: 0.10, speedGain: 1.05, head: 'iron' },
  { name: 'Pitching Wedge', loft: 46.0, length: 0.90, faceOffset: 0.036, smash: 1.24, spinK: 2.45, launchRatio: 0.72, faceBias: 0.74, axisGain: 0.85, gearGain: 0.08, speedGain: 1.00, head: 'wedge' },
  { name: 'Sand Wedge', loft: 56.0, length: 0.89, faceOffset: 0.036, smash: 1.15, spinK: 2.54, launchRatio: 0.70, faceBias: 0.72, axisGain: 0.80, gearGain: 0.05, speedGain: 1.00, head: 'wedge' },
];

/**
 * How far the head centre may pass from the ball centre and still count as a
 * strike, metres. Deliberately generous (a real driver face is ~0.12 m wide and
 * there is no haptic feedback to aim by) — `?swingzone=` overrides it, and the
 * measured miss distance feeds the strike-quality penalty either way.
 */
export const CAPTURE_RADIUS = 0.10;

/**
 * Face-size forgiveness per head shape: a driver's face is simply bigger than a
 * wedge's, and the capture zone should say so. Applied on top of CAPTURE_RADIUS
 * (or the `?swingzone=` override) by `captureRadiusFor`.
 */
const HEAD_CAPTURE_SCALE: Record<ClubHeadShape, number> = {
  wood: 1.15,
  iron: 1.0,
  wedge: 0.9,
};

/** The clean-capture radius for a club: base radius × its head's face size. */
export function captureRadiusFor(base: number, club: ClubSpec): number {
  return base * HEAD_CAPTURE_SCALE[club.head];
}

/**
 * Grazing band: a pass this far beyond the clean-capture radius still makes
 * contact — a heavily penalised thin/fat strike instead of nothing. Golf+ feel:
 * near-misses produce embarrassing shots, not silence. (The strike-quality
 * clamp in deriveShot maps the whole band to maximum penalty automatically.)
 */
export const GRAZE_FACTOR = 1.3;

/**
 * Strike-quality shape, game-first: contact inside the sweet zone IS flush —
 * full smash, no penalty — because "you found the ball" should feel pure, the
 * way Golf+ plays it. Quality only starts costing speed between the sweet zone
 * and the capture edge (mild), and gets harsh in the graze band. Shot SHAPE is
 * deliberately not gated by this: gear effect and face/path work at any
 * quality, so toe-draws, hooks and slices all survive.
 */
/** Fraction of the capture radius that counts as flush. */
const SWEET_ZONE = 0.55;
/** Ball-speed retention at the capture edge… */
const EDGE_RETENTION = 0.88;
/** …and at the far edge of the graze band (a thin, but in play). */
const GRAZE_RETENTION = 0.55;
/** Ball-speed loss per degree of spin loft beyond the club's ideal. Kept low:
 *  chips and flops add spin loft on purpose and should still pop off the face. */
const SPIN_LOFT_SPEED_LOSS = 0.006;

/**
 * Curvature responsiveness: multiplies the measured face-to-path differential
 * (plus gear effect) before it tilts the spin axis. 1.0 = the raw D-plane —
 * physically accurate, now that the flight model's lift is physical too
 * (Cl(S) in ballPhysics rather than a hard cap that over-curved everything).
 * `?shape=` still scales it for players who want arcade curve.
 */
const SHAPE_GAIN = 1.0;
/**
 * How much of the curvature the assist is allowed to remove. Deliberately
 * small: assist exists to tame the START LINE (a wild face reading shouldn't
 * fire the ball sideways), but shot shape is the player's input — face angle
 * vs club path — and erasing it made fades/draws impossible. 0.25 keeps ~88%
 * of the measured curve at the default assist of 0.5.
 */
const CURVE_ASSIST_DAMP = 0.25;
/** Spin change per cm of high/low strike (vertical gear effect). */
const VERTICAL_GEAR_SPIN = 0.10;
/** Launch change per cm of high/low strike, degrees. */
const VERTICAL_GEAR_LAUNCH = 0.5;
/** Floor on launch angle — below 1° the flight sim switches to putt physics. */
const MIN_LAUNCH_ANGLE = 1.5;

/** Measured club delivery at the moment of impact. */
export type Impact = {
  /** Head speed, m/s. */
  clubSpeed: number;
  /** Degrees, + = up. */
  attackAngle: number;
  /** Degrees, + = right of target (in-to-out for a right-hander). */
  clubPath: number;
  /** Degrees, + = face open / pointing right of target. */
  faceToTarget: number;
  /** Degrees of loft actually presented at impact. */
  dynamicLoft: number;
  /** Strike offset across the face, metres, + = toward the toe. */
  strikeToe: number;
  /** Strike offset up the face, metres, + = high. */
  strikeHigh: number;
  /** Closest approach of head centre to ball centre, metres. */
  missDistance: number;
};

export type ShotFromImpactOptions = {
  /** Capture radius the miss distance is normalised against. */
  captureRadius?: number;
  /**
   * 1 for a right-handed player, -1 for a left-hander. Only the gear-effect
   * term needs it: a toe strike always curves the ball toward the player's
   * draw side, which is left in the absolute frame for a right-hander and
   * right for a left-hander. Everything else in the model is measured
   * target-relative and so is already handedness-neutral.
   */
  handSign?: 1 | -1;
  /**
   * Shot-shaping assist, 0..1 — the Golf+-style "swing smoothing", the thing
   * that makes the mode fun rather than a physics exam. It tames the START
   * LINE (face blended toward path — a wild face reading shouldn't fire the
   * ball sideways) and, at half strength, attack angle, dynamic loft and
   * strike offsets. What it deliberately does NOT tame is the curvature: the
   * face-to-path differential that tilts the spin axis is kept nearly raw
   * (see CURVE_ASSIST_DAMP), so fades, draws, hooks and slices remain the
   * player's to hit at any assist level. Speed is never touched. 0 = raw
   * measurement everywhere; ~0.5 feels like Golf+.
   */
  assist?: number;
  /**
   * Ball-speed multiplier (a literal smash-factor boost), applied after the
   * physics. An indoor controller swing is naturally more compact than a real
   * driver swing; ~1.15 buys the missing distance back without inflating the
   * measured club speed on the HUD.
   */
  power?: number;
  /**
   * Scales the per-club speed assist as a whole. 1 = the tuned bag, 0 = pure
   * physics with no help at all, >1 = more. Separate from `power` because they
   * answer different questions: `power` is "everything a bit further",
   * `clubGain` is "how much should the driver be flattered relative to a wedge".
   */
  clubGain?: number;
  /** Curvature responsiveness override (see SHAPE_GAIN). 0 = no curve ever. */
  shape?: number;
};

/** Derived numbers worth logging or showing while tuning on the mat. */
export type ImpactDerived = {
  spinLoft: number;
  faceToPath: number;
  strikeQuality: number;
  smashEffective: number;
};

/**
 * Full derivation, for `?swingdebug=1` and the desktop model checks.
 * `shotFromImpact` is the thin wrapper the app actually calls.
 */
export function deriveShot(
  impact: Impact,
  club: ClubSpec,
  options: ShotFromImpactOptions = {},
): { shot: OpenGolfSim.Shot; derived: ImpactDerived } {
  const captureRadius = options.captureRadius ?? CAPTURE_RADIUS;
  const handSign = options.handSign ?? 1;
  // 0.5, not 0 — the value this file's own tuning notes are written against,
  // and what the range has always passed. Defaulting to raw measurements made
  // every caller that forgot to pass it silently the hardest possible version.
  const assist = THREE.MathUtils.clamp(options.assist ?? 0.5, 0, 1);
  const power = THREE.MathUtils.clamp(options.power ?? 1, 0.5, 1.6);
  const clubGain = THREE.MathUtils.clamp(options.clubGain ?? 1, 0, 2);
  const shape = THREE.MathUtils.clamp(options.shape ?? SHAPE_GAIN, 0, 3);
  const clubSpeedMph = UnitConversions.metersPerSecondToMilesPerHour(Math.max(0, impact.clubSpeed));

  // Shot-shaping assist, three layers. Face at full assist strength (it's the
  // noisiest measurement and the biggest wrecker of shots); attack angle and
  // dynamic loft at half strength toward the club's typical delivery (an indoor
  // chop shouldn't fly like a moonball); strike offsets shrunk toward centre
  // (better contact, higher effective smash). All effective values are reported
  // in shot.club, so the HUD always agrees with the flight it produced.
  const faceEffective = THREE.MathUtils.lerp(impact.faceToTarget, impact.clubPath, assist);
  const attackTypical = club.head === 'wood' ? 1.5 : -3.5;
  const attackAngle = THREE.MathUtils.lerp(impact.attackAngle, attackTypical, assist * 0.5);
  const loftTypical = club.loft - (club.head === 'wood' ? 0 : 3); // hands slightly ahead
  const dynamicLoft = THREE.MathUtils.clamp(
    THREE.MathUtils.lerp(impact.dynamicLoft, loftTypical, assist * 0.5),
    -10,
    70,
  );
  const strikeScale = 1 - assist * 0.5;
  const toeCm = impact.strikeToe * 100 * strikeScale;
  const highCm = impact.strikeHigh * 100 * strikeScale;
  const missDistance = impact.missDistance * strikeScale;

  const spinLoft = dynamicLoft - attackAngle;

  // Curvature comes from the MEASURED delivery — face angle minus club path
  // (the D-plane differential), plus gear effect (a toe strike twists the head
  // toward hook spin; strongest on a driver, near-nil on a wedge). Assist is
  // deliberately almost absent here: it tames the start line above, but the
  // face-to-path differential is the player's shot-shaping input, and the
  // earlier full-strength blend was erasing fades and draws entirely.
  const faceToPath = (
    (impact.faceToTarget - impact.clubPath) * (1 - CURVE_ASSIST_DAMP * assist)
    - handSign * club.gearGain * toeCm
  ) * shape;

  // Piecewise speed retention: flush inside the sweet zone, mild falloff to
  // the capture edge, harsh only in the graze band beyond it.
  const missNorm = missDistance / Math.max(captureRadius, 1e-6);
  const strikeQuality = missNorm <= SWEET_ZONE
    ? 1
    : missNorm <= 1
      ? THREE.MathUtils.lerp(1, EDGE_RETENTION, (missNorm - SWEET_ZONE) / (1 - SWEET_ZONE))
      : THREE.MathUtils.lerp(
        EDGE_RETENTION,
        GRAZE_RETENTION,
        THREE.MathUtils.clamp((missNorm - 1) / (GRAZE_FACTOR - 1), 0, 1),
      );
  const spinLoftExcess = Math.max(0, spinLoft - (club.loft - 2));
  const smashEffective = Math.max(
    0.3,
    club.smash * (1 - SPIN_LOFT_SPEED_LOSS * spinLoftExcess) * strikeQuality
  );

  // Per-club assist, and deliberately PROGRESSIVE rather than a flat multiplier.
  //
  // A flat gain would scale every swing equally, which quietly takes the short
  // game away: a deliberate half-swing pitch with a mid iron would fly as far
  // past its target as a full one flies to it, and the player loses the ability
  // to hit anything soft. So the help ramps in with effort — none at all below
  // ASSIST_START (a touch shot is left completely alone), reaching the club's
  // full gain by ASSIST_FULL, which is about what an honest committed swing
  // produces in a living room.
  //
  // The effect is the one Golf+ gets: swing hard with a driver and it goes
  // where a driver should go, swing softly and it goes short, and nothing
  // in between is taken out of the player's hands.
  const effort = THREE.MathUtils.smoothstep(clubSpeedMph, ASSIST_START_MPH, ASSIST_FULL_MPH);
  const gain = 1 + (club.speedGain - 1) * clubGain * effort;
  const ballSpeed = Math.max(0, clubSpeedMph * gain * smashEffective * power);

  // Trackman's rule of thumb: launch ≈ 0.8 × dynamic loft, then the attack
  // angle moves the whole thing (hitting up launches higher at the same loft).
  //
  // The floor is not cosmetic: GolfBall.launchShot treats any launch under 1°
  // as a putt and skips the air phase entirely, so a thin, de-lofted, steep
  // strike would otherwise be simulated as a 110 mph putt skidding along the
  // turf instead of the low screamer it actually is.
  const verticalLaunchAngle = THREE.MathUtils.clamp(
    dynamicLoft * club.launchRatio + attackAngle + highCm * VERTICAL_GEAR_LAUNCH,
    MIN_LAUNCH_ANGLE,
    50,
  );

  // Spin loft is what generates backspin; a strike above centre reduces it.
  const spinSpeed = THREE.MathUtils.clamp(
    Math.max(1, spinLoft) * clubSpeedMph * club.spinK
      * THREE.MathUtils.clamp(1 - highCm * VERTICAL_GEAR_SPIN, 0.55, 1.6),
    0,
    13_000,
  );

  // D-plane: the spin axis is the *ratio* of side tilt to backspin, which is
  // why the same open face slices a driver and barely bends a 9 iron.
  const spinAxis = THREE.MathUtils.clamp(
    THREE.MathUtils.radToDeg(Math.atan2(faceToPath * club.axisGain, Math.max(spinLoft, 2))),
    -45,
    45,
  );

  const horizontalLaunchAngle = THREE.MathUtils.clamp(
    faceEffective * club.faceBias + impact.clubPath * (1 - club.faceBias),
    -45,
    45,
  );

  return {
    shot: {
      ballSpeed,
      verticalLaunchAngle,
      horizontalLaunchAngle,
      spinSpeed,
      spinAxis,
      // Populating this lights up the club-delivery row the ground HUD already
      // draws for launch-monitor shots (src/xr/groundHud.ts CLUB_FIELDS).
      club: {
        clubSpeed: clubSpeedMph,
        // Post-assist values throughout — the HUD must match the flight
        attackAngle,
        clubPath: impact.clubPath,
        faceToTarget: faceEffective,
        dynamicLoft,
        smashFactor: clubSpeedMph > 0 ? ballSpeed / clubSpeedMph : 0,
      },
    },
    derived: { spinLoft, faceToPath, strikeQuality, smashEffective },
  };
}

/** Measured club delivery → the shot the flight sim launches. */
export function shotFromImpact(
  impact: Impact,
  club: ClubSpec,
  options: ShotFromImpactOptions = {},
): OpenGolfSim.Shot {
  return deriveShot(impact, club, options).shot;
}

/**
 * A plausible impact for a club, with optional scatter. Used by the desktop
 * preview (no controller to swing) to exercise the model end to end.
 */
export function syntheticImpact(club: ClubSpec, scatter = 1): Impact {
  const jitter = (magnitude: number) => (Math.random() * 2 - 1) * magnitude * scatter;
  // Longer clubs are swung faster and struck on the way up
  const baseSpeedMph = 112 - (club.loft - 10.5) * 0.95;
  const attackAngle = (club.head === 'wood' ? 1.5 : -4) + jitter(1.5);
  return {
    clubSpeed: UnitConversions.milesPerHourToMetersPerSecond(baseSpeedMph + jitter(6)),
    attackAngle,
    clubPath: jitter(3),
    faceToTarget: jitter(3),
    // Shaft lean de-lofts the shorter clubs; hands ahead is the normal pattern
    dynamicLoft: club.loft - (club.head === 'wood' ? 0 : 4) + jitter(2),
    strikeToe: jitter(0.012),
    strikeHigh: jitter(0.008),
    missDistance: Math.abs(jitter(0.02)),
  };
}
